/**
 * Reusable column-sort hook for table views.
 *
 * Sort state is component-local, so it resets on page load/refresh — the
 * incoming (default) order supplied by the caller is the default view, and any
 * user-applied column sort lasts only for the current session.
 *
 * Click cycle per column: ascending → descending → cleared (back to default).
 */
import { useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: string;
  direction: SortDirection;
}

/** Extracts the comparable value for a column from a row. */
export type SortAccessor<T> = (item: T) => string | number;

export interface ColumnSort<T> {
  sortState: SortState | null;
  toggleSort: (key: string) => void;
  sortData: (data: T[]) => T[];
  /** Arrow glyph for a column header: neutral, ascending, or descending. */
  arrowFor: (key: string) => string;
}

const ARROW_NEUTRAL = '\u2195'; // ↕
const ARROW_ASC = '\u2191'; // ↑
const ARROW_DESC = '\u2193'; // ↓

/**
 * Provides sort state and helpers for a set of named, accessor-backed columns.
 * Returns the data unchanged when no column is active (default order preserved).
 */
export function useColumnSort<T>(accessors: Record<string, SortAccessor<T>>): ColumnSort<T> {
  const [sortState, setSortState] = useState<SortState | null>(null);

  const toggleSort = (key: string): void => {
    setSortState((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  };

  const sortData = (data: T[]): T[] => {
    if (!sortState) return data;
    const accessor = accessors[sortState.key];
    if (!accessor) return data;
    const sorted = [...data].sort((a, b) => {
      const aValue = accessor(a);
      const bValue = accessor(b);
      let comparison: number;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true });
      }
      return sortState.direction === 'asc' ? comparison : -comparison;
    });
    return sorted;
  };

  const arrowFor = (key: string): string => {
    if (!sortState || sortState.key !== key) return ARROW_NEUTRAL;
    return sortState.direction === 'asc' ? ARROW_ASC : ARROW_DESC;
  };

  return { sortState, toggleSort, sortData, arrowFor };
}
